/**
 * Die Markierungsebene über einem Foto — Pfeile, Kreise, Text und Freihand, die der Prüfer
 * nachträglich auf eine Aufnahme setzt.
 *
 * **Die Ebene ist eine eigene Datei neben dem Bild** (`<foto>-overlay.svg`), kein
 * eingebranntes Pixel. Das Foto bleibt damit unverändert und beglaubigt; eine Marke lässt sich
 * später verschieben oder löschen, ohne das Original anzutasten.
 *
 * **Alle Koordinaten stehen in Bildpixeln**, Ursprung oben links, und die `viewBox` trägt die
 * Bildgrösse. Damit skaliert die ganze Ebene mit dem angezeigten Bild — dreht der Prüfer das
 * iPad und wird das Bild schmaler, schrumpfen Pfeile, Striche **und Text** prozentual mit,
 * ohne dass dafür Code läuft. Eine Grösse in Bildschirmpunkten könnte das nicht: sie bliebe
 * beim Drehen stehen und wäre auf einem anderen Gerät falsch.
 */

export type MarkerKind = 'arrow' | 'ellipse' | 'rect' | 'freehand' | 'text' | 'number' | 'cover';

/** Drei Stufen; die Zahlenwerte stehen in `SCHRIFT_ANTEIL` und `STRICH_ANTEIL`. */
export type SizeStep = 'klein' | 'mittel' | 'gross';

export interface Punkt {
  x: number;
  y: number;
}

interface Basis {
  id: string;
  /** Als CSS-Farbe, wie sie die Werkzeugleiste anbietet. */
  color: string;
}

export type Marker =
  | (Basis & { kind: 'arrow'; stroke: SizeStep; von: Punkt; bis: Punkt })
  | (Basis & { kind: 'ellipse'; stroke: SizeStep; cx: number; cy: number; rx: number; ry: number })
  | (Basis & { kind: 'rect'; stroke: SizeStep; x: number; y: number; w: number; h: number })
  | (Basis & { kind: 'freehand'; stroke: SizeStep; punkte: Punkt[] })
  | (Basis & { kind: 'text'; size: SizeStep; x: number; y: number; text: string })
  | (Basis & { kind: 'number'; size: SizeStep; x: number; y: number; n: number })
  | (Basis & { kind: 'cover'; x: number; y: number; w: number; h: number });

export interface Overlay {
  /** Breite des Bildes in Pixeln — zugleich die Breite der `viewBox`. */
  width: number;
  height: number;
  /** In Stapelfolge: die letzte Marke liegt oben. */
  markers: Marker[];
}

/**
 * Schriftgrössen als Anteil der **Bildhöhe**.
 *
 * `klein` ist so gewählt, dass es in der ungünstigsten Lage lesbar bleibt: ein 4:3-Foto wird
 * auf dem iPad im Hochformat etwa 525 pt hoch gezeigt, 2,5 % davon sind rund 13 pt. Im
 * Querformat sind es rund 18 pt.
 */
export const SCHRIFT_ANTEIL: Record<SizeStep, number> = {
  klein: 0.025,
  mittel: 0.04,
  gross: 0.06,
};

/**
 * Strichstärken, ebenfalls als Anteil der Bildhöhe.
 *
 * ⚠️ **Gespreizt am 2026-09-13** (vorher 0,3 / 0,5 / 0,8 %). Auf einem 525 pt hohen Foto waren
 * das 1,6 / 2,6 / 4,2 pt — der Unterschied zwischen „klein" und „groß" war kaum zu sehen, und
 * der Nutzer hielt die Knöpfe deshalb für wirkungslos.
 *
 * ⚠️ **Am 2026-09-20 insgesamt angehoben** (von 0,3 / 0,6 / 1,0 %), Wunsch des Nutzers nach
 * dem ersten Gerätelauf: die Marken waren zu fein. Jetzt 2,1 / 4,2 / 6,8 pt auf demselben Bild.
 * Die Spreizung bleibt dieselbe — „groß" ist weiter mehr als das Dreifache von „klein".
 */
export const STRICH_ANTEIL: Record<SizeStep, number> = {
  klein: 0.004,
  mittel: 0.008,
  gross: 0.013,
};

/**
 * Eine vorhandene Marke umfärben.
 *
 * Jede Marke hat eine Farbe — auch die Deckfläche, die damit statt Schwarz etwa Weiss werden
 * kann, wenn der Untergrund dunkel ist.
 */
export function mitFarbe<T extends Marker>(marke: T, color: string): T {
  return { ...marke, color };
}

/**
 * Die Grösse einer vorhandenen Marke ändern — oder `null`, wenn sie keine hat.
 *
 * ⚠️ **Zwei verschiedene Felder.** Pfeil, Kreis, Rechteck und Freihand tragen `stroke` (die
 * Strichstärke), Text und Nummer tragen `size` (die Schriftgrösse). Die **Deckfläche** trägt
 * keines von beiden: sie ist eine gefüllte Fläche, an der es nichts zu skalieren gibt ausser
 * ihren eigenen Kanten. `null` sagt der Leiste, dass sie die Grössenknöpfe abschalten soll,
 * statt sie tot anzubieten (Befund des Nutzers, 2026-09-13: „passierte bei mir nichts").
 */
export function mitGroesse(marke: Marker, size: SizeStep): Marker | null {
  switch (marke.kind) {
    case 'arrow':
    case 'ellipse':
    case 'rect':
    case 'freehand':
      return { ...marke, stroke: size };
    case 'text':
    case 'number':
      return { ...marke, size };
    case 'cover':
      return null;
  }
}

export const schriftPx = (size: SizeStep, bildHoehe: number) => SCHRIFT_ANTEIL[size] * bildHoehe;
export const strichPx = (size: SizeStep, bildHoehe: number) => STRICH_ANTEIL[size] * bildHoehe;

/**
 * Maskiert alles, was in XML eine eigene Bedeutung hat. Der Text kommt vom Prüfer: `<2 cm` ist
 * eine übliche Befundangabe und zerlegte das Dokument ungeschützt.
 */
const esc = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Zwei Nachkommastellen genügen bei Bildpixeln und halten Fliesskommaschwänze heraus. */
const z = (v: number) => Number(v.toFixed(2)).toString();
const pkt = (p: Punkt) => z(p.x) + ',' + z(p.y);

/**
 * Pfeilspitze als Teil **desselben** Pfades: ein eigenes Element wäre eine zweite Marke, die
 * beim Auswählen und Löschen getrennt behandelt werden müsste.
 */
function pfeilPfad(von: Punkt, bis: Punkt, breite: number): string {
  const winkel = Math.atan2(bis.y - von.y, bis.x - von.x);
  const laenge = breite * 4;
  const fluegel = (abweichung: number): Punkt => ({
    x: bis.x - laenge * Math.cos(winkel - abweichung),
    y: bis.y - laenge * Math.sin(winkel - abweichung),
  });
  const oben = fluegel(0.45);
  const unten = fluegel(-0.45);
  return (
    'M' + pkt(von) + ' L' + pkt(bis) +
    ' M' + pkt(oben) + ' L' + pkt(bis) + ' L' + pkt(unten)
  );
}

/**
 * Der Rand um eine Beschriftung.
 *
 * ⚠️ **Ohne ihn verschwindet der Text auf unruhigem Untergrund** — im Video besonders, weil
 * sich der Untergrund unter ihm **bewegt** (Wunsch des Nutzers, 2026-09-20).
 *
 * ⚠️ **Er kehrt sich um, wo der Text selbst hell ist.** Weiss ist eine der sechs Farben, gerade
 * für dunkle Aufnahmen; ein weisser Rand um weissen Text wäre unsichtbar und der Text damit
 * wieder so schlecht lesbar wie vorher.
 *
 * `paint-order="stroke"` legt den Rand **unter** die Füllung. Ohne das frässe der Strich von
 * aussen in die Buchstaben hinein und machte sie an dünnen Stellen dünner statt lesbarer.
 */
function randAttr(farbe: string, schriftGroesse: number): string {
  return (
    ' stroke="' + (istHell(farbe) ? '#111111' : '#ffffff') + '"' +
    ' stroke-width="' + z(schriftGroesse * 0.16) + '"' +
    ' stroke-linejoin="round" paint-order="stroke"'
  );
}

/**
 * Ob eine Farbe so hell ist, dass ein weisser Rand darauf verschwände.
 *
 * Die Schwelle liegt bei 70 % wahrgenommener Helligkeit (Rec. 601). Von den sechs Farben der
 * Werkzeugleiste liegt nur Weiss darüber; Gelb (`#f5a524`) kommt mit 0,68 knapp darunter
 * durch — dort ist ein weisser Rand noch der bessere, weil die Schrift selbst schon hell ist
 * und ein schwarzer Rand sie zuschnürte.
 */
function istHell(farbe: string): boolean {
  const treffer = /^#([0-9a-f]{6})$/i.exec(farbe.trim());
  if (!treffer) return false;
  const wert = parseInt(treffer[1], 16);
  const r = (wert >> 16) & 255;
  const g = (wert >> 8) & 255;
  const b = wert & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
}

/** Gemeinsame Attribute jeder Marke — sie tragen das Modell, nicht nur die Darstellung. */
const basisAttr = (m: Marker) =>
  ' data-id="' + esc(m.id) + '" data-kind="' + m.kind + '" data-color="' + esc(m.color) + '"';

const strichAttr = (m: Marker & { stroke: SizeStep }, hoehe: number) =>
  ' data-stroke="' + m.stroke + '" stroke="' + esc(m.color) +
  '" stroke-width="' + z(strichPx(m.stroke, hoehe)) +
  '" stroke-linecap="round" stroke-linejoin="round" fill="none"';

function marke(m: Marker, hoehe: number): string {
  switch (m.kind) {
    case 'arrow':
      return (
        '<path' + basisAttr(m) +
        ' data-von="' + pkt(m.von) + '" data-bis="' + pkt(m.bis) + '"' +
        strichAttr(m, hoehe) +
        ' d="' + pfeilPfad(m.von, m.bis, strichPx(m.stroke, hoehe)) + '"/>'
      );

    case 'ellipse':
      return (
        '<ellipse' + basisAttr(m) +
        ' data-cx="' + z(m.cx) + '" data-cy="' + z(m.cy) +
        '" data-rx="' + z(m.rx) + '" data-ry="' + z(m.ry) + '"' +
        strichAttr(m, hoehe) +
        ' cx="' + z(m.cx) + '" cy="' + z(m.cy) + '" rx="' + z(m.rx) + '" ry="' + z(m.ry) + '"/>'
      );

    case 'rect':
      return (
        '<rect' + basisAttr(m) +
        ' data-x="' + z(m.x) + '" data-y="' + z(m.y) +
        '" data-w="' + z(m.w) + '" data-h="' + z(m.h) + '"' +
        strichAttr(m, hoehe) +
        ' x="' + z(m.x) + '" y="' + z(m.y) + '" width="' + z(m.w) + '" height="' + z(m.h) + '"/>'
      );

    case 'freehand':
      return (
        '<path' + basisAttr(m) +
        ' data-punkte="' + m.punkte.map(pkt).join(' ') + '"' +
        strichAttr(m, hoehe) +
        ' d="' + m.punkte.map((p, i) => (i === 0 ? 'M' : 'L') + pkt(p)).join(' ') + '"/>'
      );

    case 'text': {
      const gross = schriftPx(m.size, hoehe);
      return (
        '<text' + basisAttr(m) +
        ' data-size="' + m.size + '" data-x="' + z(m.x) + '" data-y="' + z(m.y) + '"' +
        ' x="' + z(m.x) + '" y="' + z(m.y) + '" fill="' + esc(m.color) + '"' +
        ' font-size="' + z(gross) + '"' +
        ' font-family="system-ui, sans-serif" dominant-baseline="hanging"' +
        randAttr(m.color, gross) + '>' +
        esc(m.text) + '</text>'
      );
    }

    case 'number': {
      // Kreis und Ziffer gehören zusammen; die Gruppe trägt deshalb das Modell, damit die
      // Marke beim Zurücklesen **eine** bleibt.
      const gross = schriftPx(m.size, hoehe);
      const r = gross * 0.8;
      return (
        '<g' + basisAttr(m) +
        ' data-size="' + m.size + '" data-x="' + z(m.x) + '" data-y="' + z(m.y) +
        '" data-n="' + String(m.n) + '">' +
        '<circle cx="' + z(m.x) + '" cy="' + z(m.y) + '" r="' + z(r) +
        '" fill="' + esc(m.color) + '"/>' +
        '<text x="' + z(m.x) + '" y="' + z(m.y) + '" fill="#ffffff"' +
        ' font-size="' + z(gross) + '" font-family="system-ui, sans-serif"' +
        ' text-anchor="middle" dominant-baseline="central">' + String(m.n) + '</text>' +
        '</g>'
      );
    }

    case 'cover':
      // Volle Füllung, kein Rand: die Fläche soll verdecken. Sie verdeckt allerdings nur in
      // dieser Ebene — das Foto darunter bleibt unverändert lesbar (s. Spezifikation §5.5).
      return (
        '<rect' + basisAttr(m) +
        ' data-x="' + z(m.x) + '" data-y="' + z(m.y) +
        '" data-w="' + z(m.w) + '" data-h="' + z(m.h) + '"' +
        ' x="' + z(m.x) + '" y="' + z(m.y) + '" width="' + z(m.w) + '" height="' + z(m.h) + '"' +
        ' fill="' + esc(m.color) + '"/>'
      );
  }
}

/**
 * Schreibt die Ebene als eigenständiges SVG.
 *
 * `data-relay-overlay="1"` ist die Erkennungsmarke: nur damit nimmt `svgToOverlay` die Datei
 * als bearbeitbare Ebene an. Ebenen von vor dem 2026-10-01 tragen noch `data-metis-overlay`
 * (der alte Servername) — `svgToOverlay` liest beide, geschrieben wird nur der neue. `data-image` nennt den Dateinamen des Bildes, zu dem sie gehört —
 * die Zuordnung steht damit **in** der Datei und nicht nur in ihrem Namen.
 */
export function overlayToSvg(overlay: Overlay, bildDatei: string): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg"' +
    ' viewBox="0 0 ' + z(overlay.width) + ' ' + z(overlay.height) + '"' +
    ' data-relay-overlay="1" data-image="' + esc(bildDatei) + '">' +
    overlayInnerSvg(overlay) +
    '</svg>'
  );
}

/**
 * Eine Ebene liess sich nicht lesen.
 *
 * Ein eigener Fehlertyp, damit die Grossansicht ihn von jedem anderen unterscheiden und
 * gezielt reagieren kann: Hinweis zeigen, Bearbeiten sperren, **nichts** schreiben.
 */
export class OverlayLeseFehler extends Error {
  constructor(grund: string) {
    super(grund);
    this.name = 'OverlayLeseFehler';
  }
}

const SIZE_STEPS: SizeStep[] = ['klein', 'mittel', 'gross'];

function pflichtAttr(el: Element, name: string): string {
  const wert = el.getAttribute(name);
  if (wert === null) {
    throw new OverlayLeseFehler('Der Marke fehlt das Attribut ' + name + '.');
  }
  return wert;
}

function zahlAttr(el: Element, name: string): number {
  const wert = Number(pflichtAttr(el, name));
  if (!Number.isFinite(wert)) {
    throw new OverlayLeseFehler('Das Attribut ' + name + ' ist keine Zahl.');
  }
  return wert;
}

function stufeAttr(el: Element, name: string): SizeStep {
  const wert = pflichtAttr(el, name);
  const treffer = SIZE_STEPS.find((s) => s === wert);
  if (!treffer) throw new OverlayLeseFehler('Unbekannte Grössenstufe: ' + wert);
  return treffer;
}

function punktAttr(el: Element, name: string): Punkt {
  const [x, y] = pflichtAttr(el, name).split(',').map(Number);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    throw new OverlayLeseFehler('Der Punkt in ' + name + ' ist unlesbar.');
  }
  return { x, y };
}

function leseMarke(el: Element): Marker {
  const id = pflichtAttr(el, 'data-id');
  const color = pflichtAttr(el, 'data-color');
  const art = pflichtAttr(el, 'data-kind');

  switch (art) {
    case 'arrow':
      return {
        id, color, kind: 'arrow', stroke: stufeAttr(el, 'data-stroke'),
        von: punktAttr(el, 'data-von'), bis: punktAttr(el, 'data-bis'),
      };
    case 'ellipse':
      return {
        id, color, kind: 'ellipse', stroke: stufeAttr(el, 'data-stroke'),
        cx: zahlAttr(el, 'data-cx'), cy: zahlAttr(el, 'data-cy'),
        rx: zahlAttr(el, 'data-rx'), ry: zahlAttr(el, 'data-ry'),
      };
    case 'rect':
      return {
        id, color, kind: 'rect', stroke: stufeAttr(el, 'data-stroke'),
        x: zahlAttr(el, 'data-x'), y: zahlAttr(el, 'data-y'),
        w: zahlAttr(el, 'data-w'), h: zahlAttr(el, 'data-h'),
      };
    case 'freehand': {
      const roh = pflichtAttr(el, 'data-punkte').trim();
      const punkte = roh === '' ? [] : roh.split(' ').map((paar) => {
        const [x, y] = paar.split(',').map(Number);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          throw new OverlayLeseFehler('Ein Freihandpunkt ist unlesbar: ' + paar);
        }
        return { x, y };
      });
      return { id, color, kind: 'freehand', stroke: stufeAttr(el, 'data-stroke'), punkte };
    }
    case 'text':
      return {
        id, color, kind: 'text', size: stufeAttr(el, 'data-size'),
        x: zahlAttr(el, 'data-x'), y: zahlAttr(el, 'data-y'),
        // `textContent` liefert den Text bereits entmaskiert — die Maskierung beim Schreiben
        // ist damit vollständig rückgängig, ohne sie hier ein zweites Mal nachzubilden.
        text: el.textContent ?? '',
      };
    case 'number':
      return {
        id, color, kind: 'number', size: stufeAttr(el, 'data-size'),
        x: zahlAttr(el, 'data-x'), y: zahlAttr(el, 'data-y'), n: zahlAttr(el, 'data-n'),
      };
    case 'cover':
      return {
        id, color, kind: 'cover',
        x: zahlAttr(el, 'data-x'), y: zahlAttr(el, 'data-y'),
        w: zahlAttr(el, 'data-w'), h: zahlAttr(el, 'data-h'),
      };
    default:
      // Eine Ebene aus einer neueren Fassung der App. Sie stillschweigend zu verschlucken
      // hiesse, die unbekannte Marke beim nächsten Speichern zu verlieren.
      throw new OverlayLeseFehler('Unbekannte Markenart: ' + art);
  }
}

/**
 * Liest eine Ebene zurück. Wirft bei allem, was nicht zweifelsfrei eine Relay-Ebene ist.
 *
 * **Kein stiller Rückfall auf „leer".** `sichereOverlay` löscht die Datei, wenn die Ebene leer
 * ist — eine unlesbare Datei als leer zu deuten hiesse also, ihren Inhalt allein durch Öffnen
 * und Schliessen zu vernichten. Die Grossansicht meldet stattdessen einen Lesefehler und
 * sperrt das Bearbeiten.
 *
 * (`svgToStrokes` in `ink.ts` darf dort leer zurückgeben: die Handschriftseite wird nur
 * ergänzt, und ein Absturz beim Öffnen wäre dort das grössere Übel. Hier liegt der Fall
 * andersherum, weil eine leere Ebene das Löschen auslöst.)
 */
export function svgToOverlay(svg: string): Overlay {
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new OverlayLeseFehler('Die Ebene ist beschädigt.');
  }

  const wurzel = doc.documentElement;
  const marke = wurzel.getAttribute('data-relay-overlay') ?? wurzel.getAttribute('data-metis-overlay');
  if (marke !== '1') {
    throw new OverlayLeseFehler('Die Datei ist keine Relay-Markierungsebene.');
  }

  const kasten = (wurzel.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
  const [, , width, height] = kasten;
  if (kasten.length !== 4 || !Number.isFinite(width) || !Number.isFinite(height) ||
      width <= 0 || height <= 0) {
    // Ohne viewBox fehlt der Massstab: die Koordinaten stehen in Bildpixeln und wären
    // ohne Bildgrösse nicht mehr zuzuordnen.
    throw new OverlayLeseFehler('Der Ebene fehlt eine brauchbare viewBox.');
  }

  const markers = Array.from(wurzel.querySelectorAll('[data-kind]')).map(leseMarke);
  return { width, height, markers };
}

/** Achsenparalleler Rahmen einer Marke, in Bildkoordinaten. */
export interface Rahmen {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Der Rahmen, an dem die Auswahl ihre Griffe aufhängt.
 *
 * Beim Text ist er eine **Schätzung**: die wahre Textbreite kennt nur die Schriftmaschine des
 * Geräts, und sie zu messen hiesse, die Marke erst zu zeichnen. 0,55 der Schrifthöhe je Zeichen
 * ist der übliche Mittelwert einer serifenlosen Schrift und für einen Anfasser genau genug.
 */
export function markerBox(m: Marker, bildHoehe: number): Rahmen {
  switch (m.kind) {
    case 'arrow':
      return {
        x: Math.min(m.von.x, m.bis.x),
        y: Math.min(m.von.y, m.bis.y),
        w: Math.abs(m.bis.x - m.von.x),
        h: Math.abs(m.bis.y - m.von.y),
      };
    case 'ellipse':
      return { x: m.cx - m.rx, y: m.cy - m.ry, w: m.rx * 2, h: m.ry * 2 };
    case 'rect':
    case 'cover':
      return { x: m.x, y: m.y, w: m.w, h: m.h };
    case 'freehand': {
      const xs = m.punkte.map((p) => p.x);
      const ys = m.punkte.map((p) => p.y);
      if (xs.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }
    case 'text': {
      const hoehe = schriftPx(m.size, bildHoehe);
      return { x: m.x, y: m.y, w: m.text.length * hoehe * 0.55, h: hoehe };
    }
    case 'number': {
      const r = schriftPx(m.size, bildHoehe) * 0.8;
      return { x: m.x - r, y: m.y - r, w: r * 2, h: r * 2 };
    }
  }
}

/** Verschiebt eine Marke um dx/dy in Bildkoordinaten. */
export function verschiebeMarker(m: Marker, dx: number, dy: number): Marker {
  switch (m.kind) {
    case 'arrow':
      return {
        ...m,
        von: { x: m.von.x + dx, y: m.von.y + dy },
        bis: { x: m.bis.x + dx, y: m.bis.y + dy },
      };
    case 'ellipse':
      return { ...m, cx: m.cx + dx, cy: m.cy + dy };
    case 'freehand':
      return { ...m, punkte: m.punkte.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    case 'rect':
    case 'cover':
    case 'text':
    case 'number':
      return { ...m, x: m.x + dx, y: m.y + dy };
  }
}

/**
 * Skaliert eine Marke um einen Ankerpunkt.
 *
 * Text und Nummer haben keine freie Grösse — sie kennen nur die drei Stufen, damit ihre Grösse
 * ein Anteil der Bildhöhe bleibt und beim Drehen prozentual mitläuft. Sie werden deshalb nur
 * **verschoben**, nicht gedehnt; eine gedehnte Schrift wäre auch nicht mehr die gewählte Stufe.
 */
export function skaliereMarker(
  m: Marker,
  faktorX: number,
  faktorY: number,
  anker: Punkt,
): Marker {
  const sx = (x: number) => anker.x + (x - anker.x) * faktorX;
  const sy = (y: number) => anker.y + (y - anker.y) * faktorY;

  switch (m.kind) {
    case 'arrow':
      return {
        ...m,
        von: { x: sx(m.von.x), y: sy(m.von.y) },
        bis: { x: sx(m.bis.x), y: sy(m.bis.y) },
      };
    case 'ellipse':
      return {
        ...m,
        cx: sx(m.cx),
        cy: sy(m.cy),
        rx: Math.abs(m.rx * faktorX),
        ry: Math.abs(m.ry * faktorY),
      };
    case 'freehand':
      return { ...m, punkte: m.punkte.map((p) => ({ x: sx(p.x), y: sy(p.y) })) };
    case 'rect':
    case 'cover':
      return {
        ...m,
        x: sx(m.x),
        y: sy(m.y),
        w: Math.abs(m.w * faktorX),
        h: Math.abs(m.h * faktorY),
      };
    case 'text':
    case 'number':
      return { ...m, x: sx(m.x), y: sy(m.y) };
  }
}

/**
 * Nur die Marken als Markup, ohne das umgebende `<svg>` — für die Anzeige in der Grossansicht.
 *
 * Bewusst dasselbe Markup, das später in der Datei steht: was der Prüfer sieht, ist damit
 * Zeichen für Zeichen das, was gespeichert wird. Eine zweite Zeichenroutine für den Bildschirm
 * wäre eine zweite Quelle der Wahrheit und liefe der Datei mit der Zeit davon.
 */
export function overlayInnerSvg(overlay: Overlay): string {
  return overlay.markers.map((m) => marke(m, overlay.height)).join('');
}
